import { useEffect, useState } from "react";
import {
  useBranding,
  useUploadPlatformLogo,
  useUpdatePlatformAppName,
  type BrandingResponse,
  type PlatformLogoVariant,
} from "../api/hooks";
import { Button, Card, ErrorNotice, Field, Input, Notice, PageHeader } from "../components/ui";
import { DEFAULT_APP_NAME } from "../lib/branding";

/**
 * Marca de la aplicación (CLAUDE.md Fase 4, ampliación).
 *
 * Solo super-admin llega a esta pantalla (el menú y la ruta ya lo filtran,
 * ver `permissions.ts`), y el servidor lo vuelve a comprobar en la propia
 * ruta de subida (`actor.isSuperAdmin` en `panel.ts`).
 *
 * Distinta de "Marca y plantillas" (`/plantillas`): aquella es el logo que
 * va DENTRO del PDF del DeCA, por tenant/cargador. Esta es el icono/logo de
 * la APLICACIÓN en sí, y tiene tres variantes porque cada sitio donde se
 * muestra necesita un tratamiento distinto:
 *   - `icon`: icono cuadrado, app móvil (cabecera y login).
 *   - `wordmarkLight`: logotipo horizontal con texto oscuro, para fondos
 *     claros del panel (pantalla de acceso).
 *   - `wordmarkDark`: el mismo logotipo con texto claro, para fondos
 *     oscuros del panel (barra lateral).
 * Es una única configuración global a toda la plataforma, no por tenant.
 */

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

interface VariantConfig {
  variant: PlatformLogoVariant;
  title: string;
  hint: string;
  previewKey: keyof BrandingResponse;
  previewBg: string;
}

const VARIANTS: VariantConfig[] = [
  {
    variant: "icon",
    title: "Icono (app móvil)",
    hint: "PNG o JPG, hasta 700 KB. Imagen cuadrada: se usa en la cabecera y el login de la app del conductor.",
    previewKey: "icon",
    previewBg: "border border-ink-200 bg-ink-50 dark:border-ink-700 dark:bg-ink-900",
  },
  {
    variant: "wordmarkLight",
    title: "Logo para fondo claro (login del panel)",
    hint: "PNG o JPG, hasta 700 KB. Logotipo horizontal con texto oscuro: se usa en la pantalla de acceso del panel.",
    previewKey: "wordmarkLight",
    previewBg: "border border-ink-200 bg-white",
  },
  {
    variant: "wordmarkDark",
    title: "Logo para fondo oscuro (barra lateral)",
    hint: "PNG o JPG, hasta 700 KB. Mismo logotipo horizontal pero con texto claro: se usa en la barra lateral del panel.",
    previewKey: "wordmarkDark",
    previewBg: "border border-ink-700 bg-ink-950",
  },
];

function VariantUploader({ config, branding }: { config: VariantConfig; branding: ReturnType<typeof useBranding> }) {
  const upload = useUploadPlatformLogo();
  const [justUploaded, setJustUploaded] = useState(false);
  const inputId = `platform-logo-input-${config.variant}`;
  const currentUrl = branding.data?.[config.previewKey] ?? null;

  return (
    <Card className="max-w-xl">
      <p className="text-sm font-semibold text-ink-800 dark:text-ink-100">{config.title}</p>

      <div className="mt-3 flex items-center gap-4">
        <div className={`flex h-16 w-32 flex-none items-center justify-center rounded-md ${config.previewBg}`}>
          {currentUrl ? (
            <img src={currentUrl} alt={`${config.title} actual`} className="h-full w-full object-contain p-1" />
          ) : (
            <span className="text-xs text-ink-500 dark:text-ink-400">Sin imagen</span>
          )}
        </div>
        <p className="text-xs text-ink-500 dark:text-ink-400">
          {currentUrl
            ? "Esta es la imagen que se ve ahora mismo en ese lugar."
            : "Todavía no se ha subido ninguna imagen para esta variante: se usa el branding por defecto."}
        </p>
      </div>

      <div className="mt-4 border-t border-ink-200 pt-4 dark:border-ink-800">
        <Field label="Subir nueva imagen" htmlFor={inputId} hint={config.hint}>
          <input
            id={inputId}
            type="file"
            accept="image/png,image/jpeg"
            className="field"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setJustUploaded(false);
              void fileToBase64(file).then((base64) =>
                upload.mutate(
                  { variant: config.variant, filename: file.name, contentType: file.type, base64 },
                  { onSuccess: () => setJustUploaded(true) },
                ),
              );
              event.target.value = "";
            }}
          />
        </Field>

        {upload.isPending ? <p className="mt-2 text-sm text-ink-500 dark:text-ink-400">Subiendo…</p> : null}
        {upload.isError ? (
          <div className="mt-2">
            <ErrorNotice error={upload.error} />
          </div>
        ) : null}
        {justUploaded && upload.isSuccess ? (
          <div className="mt-2">
            <Notice tone="ok">Imagen actualizada. Los usuarios la verán al recargar la app o el panel.</Notice>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function AppNameEditor({ branding }: { branding: ReturnType<typeof useBranding> }) {
  const update = useUpdatePlatformAppName();
  const [value, setValue] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  // Sincroniza el campo con el valor guardado cuando llega/cambia (p. ej. al
  // entrar en la página, o tras guardar y refrescar la query).
  useEffect(() => {
    if (branding.data) setValue(branding.data.appName ?? "");
  }, [branding.data]);

  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= 60;

  return (
    <Card className="max-w-xl">
      <p className="text-sm font-semibold text-ink-800 dark:text-ink-100">Nombre de la aplicación</p>
      <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
        Texto que se muestra junto al icono por defecto (cuando no hay wordmark subido) y en la
        pestaña del navegador del panel. Si se deja vacío se usa &ldquo;{DEFAULT_APP_NAME}&rdquo;.
        No cambia el nombre de la app móvil en el teléfono del conductor: eso requiere publicar
        una nueva versión de la app, no es algo que se pueda ajustar desde aquí.
      </p>

      <div className="mt-4 border-t border-ink-200 pt-4 dark:border-ink-800">
        <Field label="Nombre" htmlFor="platform-app-name-input" hint="Entre 1 y 60 caracteres.">
          <Input
            id="platform-app-name-input"
            value={value}
            maxLength={60}
            placeholder={DEFAULT_APP_NAME}
            onChange={(event) => {
              setValue(event.target.value);
              setJustSaved(false);
            }}
          />
        </Field>

        <div className="mt-3">
          <Button
            variant="primary"
            disabled={!canSave || update.isPending}
            onClick={() => {
              update.mutate(trimmed, { onSuccess: () => setJustSaved(true) });
            }}
          >
            {update.isPending ? "Guardando…" : "Guardar nombre"}
          </Button>
        </div>

        {update.isError ? (
          <div className="mt-2">
            <ErrorNotice error={update.error} />
          </div>
        ) : null}
        {justSaved && update.isSuccess ? (
          <div className="mt-2">
            <Notice tone="ok">Nombre actualizado. Los usuarios lo verán al recargar la app o el panel.</Notice>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function PlatformBrandingPage() {
  const branding = useBranding();

  return (
    <>
      <PageHeader
        eyebrow="Plataforma"
        title="Marca de la aplicación"
        description="Nombre e imágenes de marca de la app: el icono para la app móvil del conductor y el logotipo horizontal del panel, en sus dos variantes de color según el fondo. Es una única configuración para toda la plataforma, no por empresa."
      />

      <div className="flex flex-col gap-5">
        <AppNameEditor branding={branding} />
        {VARIANTS.map((config) => (
          <VariantUploader key={config.variant} config={config} branding={branding} />
        ))}
      </div>
    </>
  );
}
